import type { InboxEntry, Suggestion, Triage } from '@brain/contracts'
import { Button, Chip, cn, EmptyState, Kbd, toast } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  CalendarClock,
  CheckSquare,
  FileText,
  FolderInput,
  Inbox,
  Info,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CaptureBar } from '../../components/CaptureBar.tsx'
import { NotePreview } from '../../components/inbox/ItemDetail.tsx'
import { formatDateTime } from '../../components/meetings/MeetingList.tsx'
import { SuggestionCard } from '../../components/meetings/SuggestionCard.tsx'
import { ThreeCol } from '../../components/ThreeCol.tsx'
import { inboxQuery, useTriage } from '../../lib/hooks.ts'
import { suggestionsQuery, useAcceptAll, useTriageV2 } from '../../lib/m2.ts'
import { isTypingTarget, useKeydown } from '../../lib/shortcuts.ts'
import { initialTriage, triageCommand, triageReducer, triageTargets } from '../../lib/triage.ts'
import { buildSuggestionPatch, groupByMeeting, type SuggestionDraft } from '../../lib/triage-patch.ts'
import { openTask, pickContainer } from '../../lib/ui-state.ts'

export const Route = createFileRoute('/_app/inbox')({
  validateSearch: (s: Record<string, unknown>): { capture?: boolean } => (s.capture ? { capture: true } : {}),
  component: InboxPage,
})

const ORIGIN_ORDER: InboxEntry['origin'][] = ['capture', 'meeting', 'agent', 'classification', 'other']
const keyOf = (e: Pick<InboxEntry, 'kind' | 'id'>) => `${e.kind}:${e.id}`
const CONFIDENT = 0.8
/** stable fallback while suggestions load or failed – a fresh [] per render re-triggered the triage effect forever */
const NO_SUGGESTIONS: Suggestion[] = []
const actionable = (s: Suggestion) => s.status === 'pending' && !s.autoAccepted

function InboxPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const q = useQuery(inboxQuery())
  const sq = useQuery(suggestionsQuery())
  const triage = useTriage()
  const triageV2 = useTriageV2()
  const acceptAll = useAcceptAll()
  const [state, dispatch] = useReducer(triageReducer, initialTriage)
  const [drafts, setDrafts] = useState<Record<string, SuggestionDraft>>({})
  const [expanded, setExpanded] = useState<string | null>(null)
  // "Details": notes unfold their text in place, tasks open the task drawer
  const showDetails = (e: InboxEntry) =>
    e.kind === 'task' ? openTask(e.id) : setExpanded((x) => (x === keyOf(e) ? null : keyOf(e)))

  // suggestions come grouped per meeting from /inbox/suggestions (§14.3); the flat list keeps captures
  const entries = useMemo(() => (q.data ?? []).filter((e) => e.kind !== 'suggestion'), [q.data])
  const suggestions = sq.data ?? NO_SUGGESTIONS
  const meetingGroups = useMemo(() => groupByMeeting(suggestions), [suggestions])
  const groups = useMemo(
    () =>
      ORIGIN_ORDER.map((origin) => ({ origin, items: entries.filter((e) => e.origin === origin) })).filter(
        (g) => g.items.length > 0,
      ),
    [entries],
  )
  const suggestionByKey = useMemo(
    () => new Map(suggestions.filter(actionable).map((s) => [`suggestion:${s.id}`, s])),
    [suggestions],
  )
  const ordered = useMemo(() => groups.flatMap((g) => g.items), [groups])
  const byKey = useMemo(() => new Map(ordered.map((e) => [keyOf(e), e])), [ordered])
  const allKeys = useMemo(
    () => [
      ...meetingGroups.flatMap((g) => g.items.filter(actionable).map((s) => `suggestion:${s.id}`)),
      ...ordered.map(keyOf),
    ],
    [meetingGroups, ordered],
  )
  const pendingCount = suggestionByKey.size + entries.length
  // e.g. after a Notion import: many meetings with a confident project proposal → one click
  const confidentKeys = useMemo(
    () =>
      suggestions
        .filter(
          (s) =>
            actionable(s) &&
            s.kind === 'classification' &&
            Boolean(s.payload.containerId) &&
            (s.confidence ?? 0) >= CONFIDENT,
        )
        .map((s) => `suggestion:${s.id}`),
    [suggestions],
  )

  useEffect(() => dispatch({ type: 'set', keys: allKeys }), [allKeys])

  const applySuggestions = (action: 'accept' | 'discard', keys: string[], containerId?: string | null) => {
    const items = keys.map((k) => suggestionByKey.get(k)).filter((x): x is Suggestion => Boolean(x))
    if (!items.length) return 0
    dispatch({ type: 'remove', keys: items.map((s) => `suggestion:${s.id}`) })
    triageV2.mutate(
      {
        action,
        items: items.map((s) => {
          const draft = {
            ...(drafts[s.id] ?? {}),
            ...(containerId !== undefined ? { containerId: containerId ?? '' } : {}),
          }
          const patch = action === 'accept' ? buildSuggestionPatch(s, draft) : undefined
          return { kind: 'suggestion' as const, id: s.id, ...(patch ? { patch } : {}) }
        }),
      },
      {
        onSuccess: () => toast(t(`triage.done.${action}`, { count: items.length })),
        onError: () => toast(t('common.error'), { tone: 'attention' }),
      },
    )
    return items.length
  }

  const apply = (action: Triage['action'], keys: string[], containerId?: string | null) => {
    const items = keys.map((k) => byKey.get(k)).filter((e): e is InboxEntry => Boolean(e))
    if (!items.length) return
    dispatch({ type: 'remove', keys })
    triage.mutate(
      { action, items: items.map((e) => ({ kind: e.kind, id: e.id })), containerId },
      {
        onSuccess: () => toast(t(`triage.done.${action}`, { count: items.length })),
        onError: () => toast(t('common.error'), { tone: 'attention' }),
      },
    )
  }

  const run = async (cmd: ReturnType<typeof triageCommand>, keys = triageTargets(state)) => {
    switch (cmd) {
      case 'down':
        return dispatch({ type: 'move', delta: 1 })
      case 'up':
        return dispatch({ type: 'move', delta: -1 })
      case 'toggle':
        return dispatch({ type: 'toggle' })
      case 'clear':
        return dispatch({ type: 'clear' })
      case 'accept': {
        const n = applySuggestions('accept', keys)
        const withSuggestion = keys.filter((k) => byKey.get(k)?.suggestion)
        if (withSuggestion.length) apply('accept', withSuggestion)
        if (!n && !withSuggestion.length) toast(t('triage.noSuggestion'))
        return
      }
      case 'move': {
        const target = await pickContainer()
        if (target === undefined) return
        // suggestions: accept into the chosen container; captures: move
        applySuggestions('accept', keys, target)
        return apply(
          'move',
          keys.filter((k) => byKey.has(k)),
          target,
        )
      }
      case 'task':
        applySuggestions(
          'accept',
          keys.filter((k) => suggestionByKey.get(k)?.kind === 'task'),
        )
        return apply(
          'to_task',
          keys.filter((k) => byKey.get(k)?.kind === 'note'),
        )
      case 'discard':
        applySuggestions('discard', keys)
        return apply(
          'discard',
          keys.filter((k) => byKey.has(k)),
        )
      case 'open': {
        const e = byKey.get(keys[0] ?? '')
        if (e?.kind === 'note') void navigate({ to: '/n/$noteId', params: { noteId: e.id } })
        return
      }
    }
  }

  useKeydown(
    (e) => {
      if (isTypingTarget(e.target)) return
      const cmd = triageCommand(e)
      if (!cmd) return
      e.preventDefault()
      void run(cmd)
    },
    [state, byKey, suggestionByKey, drafts],
  )

  const focusedKey = state.keys[state.cursor]

  return (
    <ThreeCol
      mobile="content"
      list={
        <div className="flex flex-col gap-3 p-5 text-[13px] text-text-2">
          <h2 className="meta-label">{t('triage.keys')}</h2>
          {(
            [
              ['J/K', 'triage.key.move'],
              ['E', 'triage.key.accept'],
              ['M', 'triage.key.moveTo'],
              ['T', 'triage.key.task'],
              ['⌫', 'triage.key.discard'],
              ['X', 'triage.key.select'],
              ['↵', 'triage.key.open'],
            ] as const
          ).map(([k, label]) => (
            <p key={k} className="flex items-center gap-3">
              <Kbd className="min-w-9">{k}</Kbd>
              {t(label)}
            </p>
          ))}
        </div>
      }
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-4 md:px-8 md:py-6">
        <header data-tauri-drag-region className="flex items-center gap-3">
          <h1 className="text-[22px] text-text-1">{t('nav.inbox')}</h1>
          {pendingCount > 0 && <Chip tone="apricot">{pendingCount}</Chip>}
          <span className="flex-1" />
          {confidentKeys.length > 1 && (
            <Button size={32} variant="secondary" onClick={() => applySuggestions('accept', confidentKeys)}>
              <FolderInput size={14} />
              {t('inbox.acceptConfident', { count: confidentKeys.length })}
            </Button>
          )}
          {state.selected.length > 0 && (
            <span className="text-[12px] text-meta">
              {t('triage.selected', { count: state.selected.length })}
            </span>
          )}
        </header>
        <CaptureBar autoFocus={search.capture} />
        {meetingGroups.map((g) => {
          const pending = g.items.filter(actionable)
          return (
            <section key={g.meeting?.id ?? 'other'} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 px-1">
                <CalendarClock size={14} className="text-sky-fg" />
                <h2 className="min-w-0 flex-1 truncate meta-label">
                  {g.meeting ? (
                    <Link
                      to="/meetings/$meetingId"
                      params={{ meetingId: g.meeting.id }}
                      className="hover:text-text-1"
                    >
                      {g.meeting.title} · {formatDateTime(g.meeting.startedAt)}
                    </Link>
                  ) : (
                    t('inbox.origin.classification')
                  )}
                </h2>
                {g.meeting && pending.length > 1 && (
                  <Button
                    size={32}
                    variant="ghost"
                    onClick={() => {
                      dispatch({ type: 'remove', keys: pending.map((x) => `suggestion:${x.id}`) })
                      acceptAll.mutate(g.meeting?.id ?? '', {
                        onSuccess: (r) =>
                          toast(t('triage.done.accept', { count: r?.accepted ?? pending.length })),
                        onError: () => toast(t('common.error'), { tone: 'attention' }),
                      })
                    }}
                  >
                    {t('suggestion.acceptAll', { count: pending.length })}
                  </Button>
                )}
              </div>
              {g.items.map((sg) => {
                const key = `suggestion:${sg.id}`
                const index = state.keys.indexOf(key)
                return (
                  <SuggestionCard
                    key={sg.id}
                    s={sg}
                    focused={key === focusedKey}
                    selected={state.selected.includes(key)}
                    draft={drafts[sg.id] ?? {}}
                    onDraft={(d) => setDrafts((x) => ({ ...x, [sg.id]: d }))}
                    onFocus={() => index >= 0 && dispatch({ type: 'focus', index })}
                    onToggleSelect={(range) =>
                      index >= 0 && dispatch(range ? { type: 'range', index } : { type: 'toggle', index })
                    }
                    onAccept={() => applySuggestions('accept', [key])}
                    onDiscard={() => applySuggestions('discard', [key])}
                  />
                )
              })}
            </section>
          )
        })}
        {q.isPending ? (
          <p className="text-[13px] text-meta">{t('common.loading')}</p>
        ) : entries.length === 0 && meetingGroups.length === 0 ? (
          <EmptyState
            icon={<Inbox size={20} strokeWidth={1.9} />}
            title={t('empty.inbox')}
            description={t('empty.inboxText')}
          />
        ) : (
          groups.map((g) => (
            <section key={g.origin} className="flex flex-col gap-1.5">
              <h2 className="px-1 meta-label">{t(`inbox.origin.${g.origin}`)}</h2>
              {g.items.map((e) => {
                const key = keyOf(e)
                const index = state.keys.indexOf(key)
                const selected = state.selected.includes(key)
                return (
                  <div
                    key={key}
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    onClick={(ev) => {
                      if (ev.shiftKey) dispatch({ type: 'range', index })
                      else if (ev.metaKey || ev.ctrlKey) dispatch({ type: 'toggle', index })
                      else dispatch({ type: 'focus', index })
                    }}
                    onDoubleClick={() => void run('open', [key])}
                    onKeyDown={() => {}}
                    className={cn(
                      // InboxItem: apricot edge on the left = needs human attention (§21.4)
                      'group relative flex min-h-14 cursor-default items-start gap-3 rounded-r-card rounded-l-none border border-line-1 border-l-[3px] border-l-apricot bg-card px-4 py-3',
                      key === focusedKey && 'border-pop border-l-apricot ring-2 ring-pop/20',
                      selected && 'bg-pop-tint',
                    )}
                  >
                    <span className="mt-0.5 text-meta">
                      {e.kind === 'task' ? (
                        <CheckSquare size={16} />
                      ) : e.kind === 'suggestion' ? (
                        <Sparkles size={16} />
                      ) : (
                        <FileText size={16} />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          dispatch({ type: 'focus', index })
                          showDetails(e)
                        }}
                        aria-expanded={e.kind === 'note' ? expanded === key : undefined}
                        className="block max-w-full truncate text-left text-[14px] text-text-1 hover:text-pop-fg"
                      >
                        {e.title || t('note.untitled')}
                      </button>
                      {e.excerpt && (
                        <span className="line-clamp-2 block text-[12px] text-text-2">{e.excerpt}</span>
                      )}
                      {e.suggestion && (
                        <span className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px] text-text-2">
                          <Chip tone="apricot">
                            → {e.suggestion.containerTitle ?? t('nav.inbox')} ·{' '}
                            {Math.round(e.suggestion.confidence * 100)} %
                          </Chip>
                          <span className="truncate">{e.suggestion.rationale}</span>
                        </span>
                      )}
                      {expanded === key && e.kind === 'note' && (
                        <span className="mt-2 block">
                          <NotePreview noteId={e.id} />
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100">
                      <IconAction label={t('lists.detail.show')} onClick={() => showDetails(e)}>
                        <Info size={15} />
                      </IconAction>
                      <IconAction label={t('triage.key.moveTo')} onClick={() => void run('move', [key])}>
                        <FolderInput size={15} />
                      </IconAction>
                      {e.kind === 'note' && (
                        <IconAction label={t('triage.key.task')} onClick={() => void run('task', [key])}>
                          <CheckSquare size={15} />
                        </IconAction>
                      )}
                      <IconAction label={t('triage.key.discard')} onClick={() => void run('discard', [key])}>
                        <Trash2 size={15} />
                      </IconAction>
                    </span>
                  </div>
                )
              })}
            </section>
          ))
        )}
      </div>
    </ThreeCol>
  )
}

function IconAction({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className="flex size-11 items-center justify-center rounded-pill text-meta hover:bg-grey-2 hover:text-text-1 md:size-8"
    >
      {children}
    </button>
  )
}
